import { useAction, useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Button, Typography, useHapticFeedback } from '@blade/ui-kit'
import { openBuyPremiumFlowEvent } from '@root/entities/premium'
import { ExpiredPremiumCard, PremiumText } from '@root/entities/premium/ui'
import { userPremiumTabAtom } from '@root/features/premium/premium-tabs'
import { useAppRoutes } from '@root/features/routing'
import { useTranslation } from '@root/shared/localization'
import { ButtonPurchase, FadeOverflow } from '@root/shared/ui-kit'

import type { PremiumMetaModel } from '@root/entities/premium/atoms'

type ProfilePremiumListPastProps = {
  pastPurchases: Array<PremiumMetaModel>
}

export const ProfilePremiumListPast: React.FC<ProfilePremiumListPastProps> = ({
  pastPurchases,
}) => {
  const { t } = useTranslation()
  const openBuyPremiumDrawer = useAction(openBuyPremiumFlowEvent)
  const { navigateToPremiumIndex } = useAppRoutes()
  const haptics = useHapticFeedback()
  const [, setTab] = useAtom(userPremiumTabAtom)

  const handlePurchaseClick = () => {
    haptics.impactOccurred('light')
    navigateToPremiumIndex()
  }

  const handleAllPastPurchasesClick = () => {
    haptics.impactOccurred('light')
    setTab('past')
    navigateToPremiumIndex()
  }

  return (
    <Root>
      <Footer>
        <Title>{t('profile.welcome.past_purchases')}</Title>
        <Separator />
        <PastPurchases variant="vertical" blockStart={8} blockEnd={32}>
          {pastPurchases.map((premium) => (
            <ExpiredPremiumCard
              key={premium.currencyAddress}
              currency={premium.currency}
              premium={premium}
              onBuyAgain={(currency) => {
                openBuyPremiumDrawer({
                  ...currency,
                  shouldCheckSafety: false,
                })
              }}
            />
          ))}
        </PastPurchases>
      </Footer>
      <ActionGroup>
        <ButtonPurchase
          product="premium"
          type="button"
          radius="rounded"
          onClick={handlePurchaseClick}
        >
          <PremiumText>{t('premium.feed.buy-premium')}</PremiumText>
        </ButtonPurchase>
        <ButtonAllPastPurchases
          variant="secondary"
          size="s"
          radius="rounded"
          onClick={handleAllPastPurchasesClick}
        >
          {t('profile.all_past_purchases')}
        </ButtonAllPastPurchases>
      </ActionGroup>
    </Root>
  )
}

const Root = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: 1fr auto;

  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset-inline: 0;
    bottom: 0;
    height: 100%;
    pointer-events: none;
    z-index: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      var(--appearance-1) 30%,
      var(--appearance-1) 100%
    );
  }
`

const ActionGroup = styled.div`
  position: relative;
  background: var(--appearance-1);
  display: grid;
  grid-auto-flow: column;
  gap: 6px;
  padding-top: 12px;

  padding-block: 8px var(--safe-bottom);
  padding-inline: 12px;
`

const Footer = styled.div`
  position: relative;
  display: grid;
  gap: 8px;
  height: 100%;
  min-height: 0;
  grid-template-rows: auto 1fr;
`

const PastPurchases = styled(FadeOverflow)`
  display: grid;
  align-content: start;
  gap: 6px;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding-block-start: 6px;
  padding-inline: 12px;
  position: relative;
`

const ButtonAllPastPurchases = styled(Button)`
  color: var(--text-12);
  border: 0.5px solid var(--background-9);
`

const Title = styled(Typography).attrs({
  variant: 'body2-bold',
  component: 'h2',
})`
  color: var(--text-12);
  text-align: center;
`

const Separator = styled.div`
  opacity: 0.2;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--text-12) 49.52%,
    transparent 100%
  );
`
